"use client";
import { Link } from "react-router-dom";
import React, { useState, useEffect } from "react";
import API from "../../api";
import {
  FaCheckCircle,
  FaSearch,
  FaLightbulb,
  FaShieldAlt,
  FaHandshake,
  FaFileContract,
  FaChartBar,
  FaUserShield,
  FaChartLine,
} from "react-icons/fa";

const ICON_MAP = {
  FaSearch: <FaSearch />,
  FaLightbulb: <FaLightbulb />,
  FaShieldAlt: <FaShieldAlt />,
  FaFileContract: <FaFileContract />,
  FaHandshake: <FaHandshake />,
  FaChartBar: <FaChartBar />,
  FaUserShield: <FaUserShield />,
  FaChartLine: <FaChartLine />,
};

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || import.meta.env.VITE_API_BASE_URL;

const About = () => {
  const [data, setData] = useState(null);

  // --- LANGUAGE SYNC LOGIC (Same as Career/Header) ---
  const [lang, setLang] = useState(localStorage.getItem("lang") || "en");

  useEffect(() => {
    const handleStorageChange = () => {
      setLang(localStorage.getItem("lang") || "en");
    };
    window.addEventListener("storage", handleStorageChange);
    // Interval check for smooth experience
    const interval = setInterval(handleStorageChange, 500);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Static translations for UI labels (Design untouched)
  const uiLabels = {
    en: {
      whatWeDo: "What We",
      do: "Do",
      process: "Our Proven Advisory Process",
      whyChoose: "Why Choose Us?",
      whoWeAre: "Who We Are",
      ourFirm: "Our firm",
      whoWeAreDesc1:
        "Rigors is a financial advisory firm licensed in Singapore, specialising in insurance solutions for businesses, corporates, and private clients. We represent a carefully selected panel of reputable insurance companies, giving our clients access to quality coverage across all major lines, with the benefit of truly personal guidance.",
      whoWeAreDesc2:
        "We are not a one-size-fits-all operation. Every client receives a personalised assessment, a considered recommendation, and an advisor who stays close long after the policy is in place.",
      howWeWork: "How We Work",
      approach: "Our approach",
      howWeWorkIntro:
        "We follow a structured process because the details matter. But we always start the same way: by listening.",
      steps: [
        {
          title: "Discovery",
          desc: "We take the time to understand your operations, assets, lifestyle, and goals.",
        },
        {
          title: "Assessment",
          desc: "We identify where you are exposed, what you already have, and where the gaps are.",
        },
        {
          title: "Recommendation",
          desc: "We present solutions from our insurer partners that genuinely fit, explained clearly and without jargon.",
        },
        {
          title: "Placement",
          desc: "We handle the paperwork, the documentation, and every step of getting you properly covered.",
        },
        {
          title: "Ongoing Care",
          desc: "We stay close, reviewing your coverage as your life evolves and supporting you whenever you need it.",
        },
      ],
      ourValues: "Our Values",
      whatDrivesUs: "What drives us",
      values: [
        {
          title: "Honesty",
          desc: "We tell you what you need to hear, not what is easiest to say.",
        },
        {
          title: "Precision",
          desc: "We go deeper than the surface to find what others miss.",
        },
        {
          title: "Care",
          desc: "Your peace of mind is the measure of our work.",
        },
        {
          title: "Loyalty",
          desc: "We are your advisor for the long term, not just the first policy.",
        },
      ],
      leadership: "LEADERSHIP",
      ourLeadership: "Our leadership",
      leadershipContent1:
        "Rigors is led by Juanna-Gaby Fung, whose career in insurance financial advisory has been defined by one thing above all: doing right by her clients. With deep knowledge across general insurance and a reputation for straight-talking, thoughtful counsel, Juanna has built a firm that reflects her values in every interaction.",
      leadershipContent2:
        "She leads a small, experienced team that shares the same standards, the same care, and the same commitment to getting it right, for every client, every time.",
      ctaTitle: "We would love to tell you more.",
      ctaHighlight: "Or better yet, we would love to hear about you.",
      ctaSub: "Reach out and let's have a proper conversation.",
      ctaBtn: "Get in Touch",
      loading: "Loading...",
    },
    zh: {
      whatWeDo: "我们的",
      do: "工作",
      process: "我们行之有效的顾问流程",
      whyChoose: "为什么选择我们？",
      whoWeAre: "我们是谁",
      ourFirm: "我们的公司",
      whoWeAreDesc1:
        "Rigors 是一家获得新加坡执照的财务咨询公司，专门为企业、公司和私人客户提供保险解决方案。我们代表一组精心挑选的知名保险公司，让我们的客户能够获得涵盖所有主要业务领域的优质保障，并受益于真正的个性化指导。",
      whoWeAreDesc2:
        "我们不是一成不变的运营模式。每位客户都会获得个性化的评估、深思熟虑的建议，以及在保单生效后长期保持联系的顾问。",
      howWeWork: "我们如何工作",
      approach: "我们的方法",
      howWeWorkIntro:
        "我们遵循结构化的流程，因为细节决定成败。但我们总是以同样的方式开始：倾听。",
      steps: [
        {
          title: "探索",
          desc: "我们花时间了解您的运营、资产、生活方式和目标。",
        },
        {
          title: "评估",
          desc: "我们识别您的风险敞口、已有的保障以及存在的缺口。",
        },
        {
          title: "建议",
          desc: "我们从保险合作伙伴中提供真正适合的解决方案，并以清晰、无术语的方式进行解释。",
        },
        {
          title: "安置",
          desc: "我们处理文书工作、文件以及为您提供妥善保障的每一个步骤。",
        },
        {
          title: "持续关怀",
          desc: "我们保持联系，随着您生活的演变审查您的保障，并在您需要时随时提供支持。",
        },
      ],
      ourValues: "我们的价值观",
      whatDrivesUs: "我们的驱动力",
      values: [
        {
          title: "诚实",
          desc: "我们告诉您需要听到的，而不是最容易说出口的。",
        },
        {
          title: "精准",
          desc: "我们深入表面之下，寻找他人遗漏的细节。",
        },
        {
          title: "关怀",
          desc: "您的安心是我们工作的衡量标准。",
        },
        {
          title: "忠诚",
          desc: "我们是您的长期顾问，不仅仅是第一份保单。",
        },
      ],
      leadership: "领导团队",
      ourLeadership: "我们的领导层",
      leadershipContent1:
        "Rigors 由 Juanna-Gaby Fung 领导，她的保险财务咨询职业生涯最核心的定义是：为客户做正确的事。凭借在一般保险领域的深厚知识以及心直口快、周到咨询的声誉，Juanna 建立了一家在每一次互动中都体现其价值观的公司。",
      leadershipContent2:
        "她领导着一支经验丰富的小型团队，他们拥有相同的标准、相同的关怀以及同样的承诺，即为每一位客户、每一次都把事情做对。",
      ctaTitle: "我们很乐意告诉您更多。",
      ctaHighlight: "或者更好的是，我们很想听听您的故事。",
      ctaSub: "联系我们，让我们进行一次真正的对话。",
      ctaBtn: "取得联系",
      loading: "加载中...",
    },
  };

  const cur = uiLabels[lang];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await API.get("/api/main-about");
        if (res.data.success) {
          setData(res.data.data);
        }
      } catch (err) {
        console.error("Data fetch error", err);
      }
    };
    fetchData();
  }, []);

  if (!data)
    return (
      <div className="py-10 text-center font-century text-brand-text">
        {cur.loading}
      </div>
    );

  return (
    <div className="overflow-x-hidden leading-normal transition-colors duration-500 bg-brand-bg text-brand-text font-arial">
      {/* 2. HISTORY */}
      <section className="py-10 md:py-10 lg:py-10 bg-white">
        <div className="container max-w-6xl px-6 mx-auto">
          <div className="grid items-stretch grid-cols-1 gap-12 lg:grid-cols-2">
            {/* LEFT */}
            <div className="flex flex-col justify-center space-y-6">
              {/* HEADING */}
              <h2 className="text-[28px] md:text-[34px] leading-tight font-[Playfair_Display] text-[#1A1A1A]">
                {data.historyHeading[lang]}
              </h2>

              {/* DESCRIPTION */}
              <div className="space-y-4 text-[14px] md:text-[16px] leading-relaxed text-justify text-[#999999]">
                <p>{data.historyDescription[lang]}</p>
              </div>
            </div>

            {/* RIGHT IMAGE */}
            <div className="relative">
              <img
                src={`${API_BASE_URL}/${data.historyImage}`}
                className="object-cover w-full h-full min-h-[350px] rounded-2xl border border-black/5 shadow-[0_20px_50px_rgba(0,0,0,0.08)]"
                alt="History"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 3. WHO WE ARE */}
      <section className="py-10 md:py-10 bg-[#F5F0E8] transition-colors">
        <div className="container max-w-4xl px-6 mx-auto text-center">
          {/* ICON + TITLE ROW */}
          <div className="flex flex-col items-center justify-center gap-2 mb-6 text-center">
            <h3 className="text-[#B08D57] uppercase tracking-[0.35em] text-[11px] uppercase font-arial font-century text-[#38bdf8]">
              {cur.ourFirm}
            </h3>

            <h2 className="text-[28px] md:text-[34px] font-bold font-century text-brand-text tracking-tight">
              {cur.whoWeAre}
            </h2>
          </div>

          {/* CONTENT */}
          <div className="space-y-5 text-[14px] md:text-[16px] leading-relaxed text-gray-600 dark:text-gray-400 font-arial max-w-6xl mx-auto">
            <p>{cur.whoWeAreDesc1}</p>
            <p>{cur.whoWeAreDesc2}</p>
          </div>
        </div>
      </section>

      {/* 4. HOW WE WORK */}
      <section className="py-10 md:py-10  bg-white">
        <div className="container max-w-6xl mx-auto">
          {/* HEADER */}
          <div className="items-center justify-center gap-2 mb-6 text-center">
            <h3 className="tracking-[0.3em] font-semibold text-[11px] uppercase font-arial font-century text-[#B08D57]">
              {cur.approach}
            </h3>

            <h2 className="text-[28px] md:text-[34px] font-[Playfair_Display] text-[#1A1A1A]">
              {cur.howWeWork}
            </h2>

            <p className="mt-4 text-[14px] md:text-[16px] text-[#999999] max-w-[260px] md:max-w-[600px] mx-auto leading-relaxed">
              {cur.howWeWorkIntro}
            </p>
          </div>

          {/* STEPS */}
          <div className="grid grid-cols-1 gap-10 md:grid-cols-5">
            {cur.steps.map((step, i) => (
              <div
                key={i}
                className="group flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-2"
              >
                {/* NUMBER */}
                <div className="mb-6">
                  <div
                    className="w-16 h-16 rounded-full bg-white border border-[#B08D57] flex items-center justify-center text-[16px] font-bold font-century text-[#B08D57] 
            transition-all duration-300 
            group-hover:bg-[#B08D57] group-hover:text-[#F5F0E8]"
                  >
                    {i + 1}
                  </div>
                </div>

                {/* TITLE */}
                <h4 className="mb-4 text-[13px] font-semibold uppercase font-century text-[#1A1A1A]">
                  {step.title}
                </h4>

                {/* DESC */}
                <p className="text-[12px] md:text-[14px] leading-relaxed text-[#999999] max-w-[260px] md:max-w-[400px] mx-auto md:mx-0">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. OUR VALUES */}
      <section className="py-10 md:py-10 bg-[#999999]/10">
        <div className="container max-w-6xl px-6 mx-auto">
          {/* HEADER */}
          <div className="mb-8 text-center">
            <h3 className=" tracking-[0.3em] font-semibold text-[11px] uppercase font-arial font-century text-[#B08D57]">
              {cur.whatDrivesUs}
            </h3>

            <h2 className="text-[28px] md:text-[34px] font-[Playfair_Display] text-[#1A1A1A]">
              {cur.ourValues}
            </h2>
          </div>

          {/* GRID */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {cur.values.map((value, idx) => (
              <div
                key={idx}
                className="group relative p-6 rounded-2xl bg-white border border-black/5 shadow-[0_10px_30px_rgba(0,0,0,0.05)] transition-all duration-500 text-center md:text-left hover:-translate-y-1"
              >
                {/* top accent line */}
                <div className="absolute top-0 left-0 w-full h-[1px] bg-[#B08D57] opacity-0 group-hover:opacity-100 transition"></div>

                {/* ICON */}
                <div className="w-11 h-11 rounded-xl bg-[#B08D57]/10 flex items-center justify-center text-[#B08D57] mb-5 mx-auto md:mx-0 group-hover:bg-[#B08D57] group-hover:text-[#F5F0E8] transition-all duration-300">
                  <FaCheckCircle size={18} />
                </div>

                {/* TITLE */}
                <h4 className="text-[16px] md:text-[17px] font-semibold font-century text-[#1A1A1A] mb-3">
                  {value.title}
                </h4>

                {/* DESC */}
                <p className="text-[14px] md:text-[15px] leading-relaxed text-[#999999]">
                  {value.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* 5.5 LEADERSHIP */}
      <section className="py-10 md:py-10 bg-white overflow-hidden">
        <div className="container max-w-6xl px-6 mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* IMAGE */}
            <div className="lg:col-span-5 relative">
              <div className="absolute -top-6 -left-6 w-28 h-28 bg-[#B08D57]/10 rounded-3xl -z-10"></div>
              <div className="absolute -bottom-6 -right-6 w-28 h-28 bg-[#B08D57]/10 rounded-3xl -z-10"></div>

              <div className="relative rounded-[32px] overflow-hidden shadow-[0_25px_70px_-20px_rgba(0,0,0,0.15)] group border-4 border-[#F5F0E8]">
                <img
                  src="/profile.png"
                  alt="Juanna-Gaby Fung"
                  className="w-full h-[460px] object-cover object-top transition-all duration-700 group-hover:scale-[1.03]"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition duration-500 flex flex-col justify-end p-6 text-[#F5F0E8]">
                  <p className="font-semibold text-[15px] tracking-wide">
                    Juanna-Gaby Fung
                  </p>
                  <p className="text-[12px] text-[#F5F0E8]/80">
                    Founder & Managing Director
                  </p>
                </div>
              </div>
            </div>

            {/* CONTENT */}
            <div className="lg:col-span-7">
              {/* TOP BADGE */}
              <div className="inline-flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-lg bg-[#B08D57]/10 flex items-center justify-center text-[#B08D57]">
                  <FaUserShield size={18} />
                </div>
                <span className="text-[#B08D57] uppercase tracking-[0.35em] font-semibold text-[11px]">
                  {cur.leadership}
                </span>
              </div>

              {/* MAIN HEADING */}
              <h2 className="text-3xl md:text-4xl font-[Playfair_Display] text-[#1A1A1A] leading-tight">
                {lang === "en" ? "Led by Conviction" : "由 信念领导"}
              </h2>

              {/* TEXT */}
              <div className="mt-6 space-y-4 text-[15px] md:text-[16px] leading-relaxed text-[#999999] max-w-2xl">
                <p>{cur.leadershipContent1}</p>
                <p>{cur.leadershipContent2}</p>
              </div>

              {/* SIGNATURE */}
              <div className="mt-10 flex items-center gap-5">
                <div className="h-[1px] flex-1 bg-[#E5E0D8]"></div>

                <div className="text-right">
                  <p className="text-[12px] font-semibold uppercase tracking-widest text-[#1A1A1A]">
                    Juanna-Gaby Fung
                  </p>
                  <p className="text-[10px] text-[#B08D57] uppercase tracking-widest font-semibold">
                    Managing Director
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* 6. CTA */}
      <section className="py-10 md:py-10 bg-white">
        <div className="container max-w-6xl px-6 mx-auto">
          {/* BOX */}
          <div className="text-center   bg-[#F5F0E8]  px-8 py-12 md:px-12 md:py-14">
            {/* TITLE */}
            <h2 className="text-3xl md:text-4xl font-[Playfair_Display] leading-tight text-[#1A1A1A]">
              {cur.ctaTitle}{" "}
              <span className="text-[#1A1A1A]">{cur.ctaHighlight}</span>
            </h2>

            {/* SUBTEXT */}
            <p className="mt-4 text-[14px] md:text-[16px] text-[#999999] max-w-[520px] mx-auto leading-relaxed">
              {cur.ctaDesc}
            </p>

            {/* CTA LINK (text style, not button) */}
            <div className="mt-8">
              <Link to="/contact">
                <button className="text-[13px] uppercase font-semibold text-[#1A1A1A] hover:text-[#B08D57] transition">
                  {cur.ctaBtn} →
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
